import * as React from 'react';
import FormPlaceholder from 'components/form-placeholder/component';
import update from 'immutability-helper';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Input,
  Message,
  Popup,
  Segment
  } from 'semantic-ui-react';
import { ConfirmationModal } from 'components/confirmation-modal/component';
import { DropdownItem } from 'types/dropdown-item';
import { ErrorMessage } from 'components/error-message';
import { getI18n, getRoleLabel } from 'services/i18n-service';
import { hasPermissionTo } from 'helpers/permission-helper';
import { isEmailValid } from 'helpers/email-helper';
import { Mode } from 'types/mode';
import { Permission } from 'types/permission';
import { Props } from './props';
import { Redirect } from 'react-router';
import { RoleType } from 'types/role-type';
import { translations } from 'types/translations';
import { User } from 'types/user';
import { UserManagementMode } from 'types/user-management-mode';

import './styles.css';

interface State {
  user: User;
  isPasswordConfirmationOpen: boolean;
  isPasswordResetRequested: boolean;
}

export class UserForm extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      user: {
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        roleType: RoleType.Unknown
      },
      isPasswordConfirmationOpen: false,
      isPasswordResetRequested: false
    } as State;
  }

  componentWillReceiveProps(nextProps: Props) {
    if (!nextProps.isProcessing && nextProps.user && nextProps.mode !== UserManagementMode.create) {
      this.setState(update(this.state, {
        user: { $set: nextProps.user }
      }));
    }
  }

  openPasswordResetConfirmation = () => {
    this.setState(update(this.state, {
      isPasswordConfirmationOpen: { $set: true }
    }));
  }

  handlePasswordResetConfirmed = () => {
    this.setState(update(this.state, {
      isPasswordResetRequested: { $set: true },
      isPasswordConfirmationOpen: { $set: false }
    }));
    this.sendPasswordReset();
  }

  handleConfirmationClose = () => {
    this.setState(update(this.state, {
      isPasswordConfirmationOpen: { $set: false }
    }));
  }

  isCreateMode = () => {
    return this.props.mode === UserManagementMode.create;
  }

  isEditMode = () => {
    return this.props.mode === UserManagementMode.edit;
  }

  isProfileMode = () => {
    return this.props.mode === UserManagementMode.profile;
  }

  submitButton = () => {
    const i18n = getI18n();
    if (this.isCreateMode()) {
      return (
        <Button type="submit"
          className="save-button neutral button align-right"
          disabled={this.invalidCreateForm() || this.props.isProcessing || !this.props.isOnline}
          onClick={this.createUser}>
          {i18n.t(translations.CreateUser)}
        </Button>
      );
    } else {
      return (
        <Button type="submit"
          className="save-button neutral button align-right"
          disabled={this.invalidEditForm() || this.props.isProcessing || !this.props.isOnline}
          onClick={this.updateUser}>
          {i18n.t(translations.Save)}
        </Button>
      );
    }
  }

  roleType = () => {
    if (this.isProfileMode()) {
      const roleLabel = getRoleLabel(this.state.user.roleType);
      return (
        <Input className="user-profile-input"
          type="text"
          name="user-profile-role-type"
          onChange={this.updateRoletype}
          disabled={true}
          value={roleLabel} />
      );
    } else {
      const dropdownItems: DropdownItem[] = [
        new DropdownItem(getRoleLabel(RoleType.TenantAdmin), RoleType.TenantAdmin.toString()),
        new DropdownItem(getRoleLabel(RoleType.Operator), RoleType.Operator.toString()),
        new DropdownItem(getRoleLabel(RoleType.Viewer), RoleType.Viewer.toString())
      ];

      // if logged in user has Optel permission, then show Optel roles
      if (hasPermissionTo([Mode.Read], Permission.Optel, this.props.permissions)) {
        dropdownItems.push(new DropdownItem(getRoleLabel(RoleType.OptelAdmin), RoleType.OptelAdmin.toString()));
        dropdownItems.push(new DropdownItem(getRoleLabel(RoleType.OptelSupport), RoleType.OptelSupport.toString()));
      }

      return (
        <Dropdown
          className="user-profile-input"
          name="user-profile-role-type"
          selection={true}
          options={dropdownItems}
          value={this.state.user.roleType.toString()}
          onChange={this.updateRoletype} />
      );
    }
  }

  public render() {
    if (this.props.requestSuccess) {
      return (<Redirect to={`${process.env.PUBLIC_URL}/users`} />);
    }

    const i18n = getI18n();
    const user = this.state.user;
    const successColor = 'green';
    const errorColor = 'red';

    if (this.props.isFirstLoading || !this.props.user) {
      return (<FormPlaceholder count={5} />);
    }

    return (
      <>
        <Form>
          <Form.Field className="username-field">
            <label className="user-profile-label">
              {i18n.t(translations.Username)}
              {
                this.isCreateMode() ?
                <Popup trigger={<Icon className="on-hover"
                    color="blue"
                    name="help circle"
                  />}>
                  {i18n.t(translations.UsernameRequirement)}
                </Popup>
                :
                  null
              }
            </label>
            <Input className="user-profile-input"
              type="text"
              name="user-profile-username"
              onChange={this.updateUsername}
              disabled={this.isEditMode() || this.isProfileMode()}
              value={this.handleNull(user.username)} />
          </Form.Field>
          <Form.Field className="first-name-field">
            <label className="user-profile-label">{i18n.t(translations.FirstName)}</label>
            <Input className="user-profile-input"
              type="text"
              name="user-profile-first-name"
              onChange={this.updateFirstname}
              disabled={this.isEditMode() || this.isProfileMode()}
              value={this.handleNull(user.firstName)} />
          </Form.Field>
          <Form.Field className="last-name-field">
            <label className="user-profile-label">{i18n.t(translations.LastName)}</label>
            <Input className="user-profile-input"
              type="text"
              name="user-profile-last-name"
              disabled={this.isEditMode() || this.isProfileMode()}
              onChange={this.updateLastname}
              value={this.handleNull(user.lastName)} />
          </Form.Field>
          <Form.Field className="role-type-field">
            <label className="user-profile-label">{i18n.t(translations.Role)}</label>
            {this.roleType()}
          </Form.Field>
          <Form.Field className="email-address-field">
            <label className="user-profile-label">{i18n.t(translations.Email)}</label>
            <Input className="user-profile-input"
              type="email"
              name="user-profile-email"
              onChange={this.updateEmail}
              value={this.handleNull(user.email)} />
          </Form.Field>
          <Form.Field>
            <ErrorMessage message={this.props.errorMessage} showErrorPrefix={false} />
          </Form.Field>
          <Form.Field>
            {this.submitButton()}
          </Form.Field>
        </Form>
        {!this.isCreateMode() ?
          <Segment className="spaced-buttons">
            <Form>
              {this.props.sendPasswordReset || this.props.children ?
                <Form.Field className="other-actions-field">
                <label className="other-actions-label">{i18n.t(translations.OtherActions)}</label>
                </Form.Field>
                : null
              }
              {this.props.sendPasswordReset &&
                <Button className="neutral reset-password"
                  disabled={this.props.isProcessing || !this.props.isOnline}
                  onClick={this.openPasswordResetConfirmation}
                  content={i18n.t(translations.ResetPassword)}
                />
              }
              {this.props.children}
              {this.state.isPasswordResetRequested && !this.props.isProcessing &&
                <Message color={this.props.isPasswordResetSuccess ? successColor : errorColor}
                  content={this.props.isPasswordResetSuccess ? i18n.t(translations.PasswordResetSuccess)
                    : this.props.passwordResetErrorMessage && i18n.t(this.props.passwordResetErrorMessage)} />
              }
            </Form>
          </Segment>
          : null
        }
        <ConfirmationModal
          isOpen={this.state.isPasswordConfirmationOpen}
          object={this.props.user}
          confirm={this.handlePasswordResetConfirmed}
          close={this.handleConfirmationClose}
          title={i18n.t(translations.ConfirmPasswordReset)}
          message={i18n.t(translations.ConfirmPasswordResetMessage, { username: this.props.user.username })}
        />
      </>
    );
  }

  private handleNull = (text: string): string => {
    if (!text) {
      return '';
    }

    return text;
  }

  private sendPasswordReset = () => {
    if (this.props.sendPasswordReset && this.props.user) {
      this.props.sendPasswordReset(this.props.user.email);
    }
  }

  private updateUsername = (event: React.FormEvent<HTMLInputElement>) => {
    var user = update(this.state.user, {
      username: { $set: event.currentTarget.value }
    });
    this.setState({
      user
    });
  }

  private updateFirstname = (event: React.FormEvent<HTMLInputElement>) => {
    var user = update(this.state.user, {
      firstName: { $set: event.currentTarget.value }
    });
    this.setState({
      user
    });
  }

  private updateLastname = (event: React.FormEvent<HTMLInputElement>) => {
    var user = update(this.state.user, {
      lastName: { $set: event.currentTarget.value }
    });
    this.setState({
      user
    });
  }

  // tslint:disable-next-line:no-any
  private updateRoletype = (event: any, { value }: any) => {
    var user = update(this.state.user, {
      roleType: { $set: value }
    });
    this.setState({
      user
    });
  }

  private updateEmail = (event: React.FormEvent<HTMLInputElement>) => {
    var user = update(this.state.user, {
      email: { $set: event.currentTarget.value }
    });
    this.setState({
      user
    });
  }

  private invalidEditForm = (): boolean => {
    const invalid = (
      this.isInvalid(this.state.user.email) || !isEmailValid(this.state.user.email)
    );
    return invalid;
  }

  private invalidCreateForm = (): boolean => {
    const invalid = (
      this.isInvalid(this.state.user.username) ||
      this.userNameInvalid(this.state.user.username) ||
      this.isInvalid(this.state.user.firstName) ||
      this.isInvalid(this.state.user.lastName) ||
      this.roleInvalid(this.state.user.roleType) ||
      this.isInvalid(this.state.user.email) ||
      !isEmailValid(this.state.user.email)
    );
    return invalid;
  }

  private isInvalid = (text: string): boolean => {
    return text === undefined ||
      text === null ||
      text === '';
  }

  private userNameInvalid = (username: string): boolean => {
    return username.length < 5 || username.length > 50;
  }

  private roleInvalid = (role: RoleType): boolean => {
    return role === undefined ||
      role === null ||
      role === RoleType.Unknown;
  }

  private updateUser = () => {
    if (this.props.user) {
      this.props.saveUser(this.props.user.id, this.state.user);
    }
  }

  private createUser = () => {
    this.props.saveUser(null, this.state.user);
  }
}


// WEBPACK FOOTER //
// ./src/components/user-form/component.tsx